/**
 * Registro normativo delle vitamine dichiarabili in etichetta nutrizionale
 * (Reg. UE 1169/2011, Allegato XIII Parte A punto 1 — 13 voci, mai emendate dal
 * 2011; consolidato verificato al 01/04/2025, CELEX 02011R1169-20250401).
 *
 * Il codice canonico `FOLIC_ACID` rispetta la denominazione legale dell'Allegato
 * XIII ("acido folico"): "folati" non e' un termine dell'Allegato e non va usato
 * altrove nel codice o nell'i18n.
 *
 * `order` fissa l'ordine di presentazione in tabella: l'art. 34(1) rimanda
 * all'Allegato XV, che colloca le vitamine per ultime, dopo il sale, nell'ordine
 * dell'Allegato XIII punto 1 (lo stesso qui codificato).
 *
 * `nrvAdults` sono i "Daily Reference Intakes for Vitamins and Minerals
 * (Adults)" dell'Allegato XIII: non valgono per lattanti/prima infanzia
 * (Reg. 609/2013 + Reg. delegato 2016/127, fuori scope) ne' per gli integratori
 * alimentari (Dir. 2002/46/CE, dichiarazione per dose giornaliera — vedi
 * `MICRONUTRIENT_REGISTRY_SCOPE_NOTE`).
 *
 * `significantDigits` segue la DG SANTE Guidance Document (dicembre 2012),
 * sezione 6 Tabella 4: 3 cifre significative per vitamina A e acido folico, 2
 * per tutte le altre. Non e' un arrotondamento a decimali fissi.
 *
 * `tolerance` distingue due bande della stessa guidance (sezioni 3 e 5):
 *  - `NATURAL`: vitamina presente naturalmente nell'alimento, tolleranza
 *    simmetrica ampia (-35% / +50%), incertezza di misura inclusa;
 *  - `ADDED_OR_CLAIM`: vitamina aggiunta (Reg. 1925/2006) o oggetto di un claim
 *    (Reg. 1924/2006) — tolleranza asimmetrica: al ribasso solo l'incertezza di
 *    misura (di fatto il valore dichiarato e' il minimo garantito a fine
 *    shelf-life), al rialzo +50%.
 * I fattori sono moltiplicativi sul valore dichiarato (es. lowerFactor 0.65 =
 * -35%). Enforcement solo documentale: il sistema non puo' validare valori
 * analitici, vedi `MICRONUTRIENT_REGISTRY_SCOPE_NOTE`.
 *
 * Sanzioni: D.lgs 231/2017 art. 15 (2.000-16.000 EUR) copre esattamente le
 * violazioni di forma/contenuto/espressione/presentazione degli artt. 30-35 e
 * degli Allegati XIII, XIV, XV; art. 16 estende alle informazioni volontarie
 * ex art. 36. Una vitamina dichiarata male costa piu' che non dichiararla.
 */
export const MICRONUTRIENT_CODES = [
  'VITAMIN_A',
  'VITAMIN_D',
  'VITAMIN_E',
  'VITAMIN_K',
  'VITAMIN_C',
  'THIAMIN',
  'RIBOFLAVIN',
  'NIACIN',
  'VITAMIN_B6',
  'FOLIC_ACID',
  'VITAMIN_B12',
  'BIOTIN',
  'PANTOTHENIC_ACID',
] as const;

export type MicronutrientCode = (typeof MICRONUTRIENT_CODES)[number];

export type MicronutrientUnit = 'MG' | 'MCG';

export type MicronutrientToleranceBasis = 'NATURAL' | 'ADDED_OR_CLAIM';

export interface MicronutrientToleranceFactor {
  /** Moltiplicatore sul valore dichiarato per il limite inferiore accettato. */
  lowerFactor: number;
  /** Moltiplicatore sul valore dichiarato per il limite superiore accettato. */
  upperFactor: number;
}

export interface MicronutrientDefinition {
  code: MicronutrientCode;
  /** Ordine di presentazione in tabella (Allegato XV), dopo il sale. */
  order: number;
  /** Denominazione legale italiana (Allegato XIII), usata in etichetta PDF. */
  labelNameIt: string;
  /** Chiave i18n per UI/documentazione (namespace nutrition.micronutrients.*). */
  i18nKey: string;
  /** Unita' legale canonica: MG o MCG, fissata per nutriente (Allegato XV). */
  unit: MicronutrientUnit;
  /** Valore nutritivo di riferimento adulti, Allegato XIII punto 1. */
  nrvAdults: number;
  /** Cifre significative per l'arrotondamento in etichetta (DG SANTE Tab. 4). */
  significantDigits: 2 | 3;
  tolerance: Record<MicronutrientToleranceBasis, MicronutrientToleranceFactor>;
}

const NATURAL_TOLERANCE: MicronutrientToleranceFactor = { lowerFactor: 0.65, upperFactor: 1.5 };
const ADDED_OR_CLAIM_TOLERANCE: MicronutrientToleranceFactor = {
  lowerFactor: 1,
  upperFactor: 1.5,
};

function definition(
  code: MicronutrientCode,
  order: number,
  labelNameIt: string,
  unit: MicronutrientUnit,
  nrvAdults: number,
  significantDigits: 2 | 3,
): MicronutrientDefinition {
  return {
    code,
    order,
    labelNameIt,
    i18nKey: `nutrition.micronutrients.${code}`,
    unit,
    nrvAdults,
    significantDigits,
    tolerance: { NATURAL: NATURAL_TOLERANCE, ADDED_OR_CLAIM: ADDED_OR_CLAIM_TOLERANCE },
  };
}

/** Ordine e valori: Allegato XIII Parte A punto 1, Reg. UE 1169/2011. */
export const MICRONUTRIENT_REGISTRY: readonly MicronutrientDefinition[] = [
  definition('VITAMIN_A', 0, 'vitamina A', 'MCG', 800, 3),
  definition('VITAMIN_D', 1, 'vitamina D', 'MCG', 5, 2),
  definition('VITAMIN_E', 2, 'vitamina E', 'MG', 12, 2),
  definition('VITAMIN_K', 3, 'vitamina K', 'MCG', 75, 2),
  definition('VITAMIN_C', 4, 'vitamina C', 'MG', 80, 2),
  definition('THIAMIN', 5, 'tiamina', 'MG', 1.1, 2),
  definition('RIBOFLAVIN', 6, 'riboflavina', 'MG', 1.4, 2),
  definition('NIACIN', 7, 'niacina', 'MG', 16, 2),
  definition('VITAMIN_B6', 8, 'vitamina B6', 'MG', 1.4, 2),
  definition('FOLIC_ACID', 9, 'acido folico', 'MCG', 200, 3),
  definition('VITAMIN_B12', 10, 'vitamina B12', 'MCG', 2.5, 2),
  definition('BIOTIN', 11, 'biotina', 'MCG', 50, 2),
  definition('PANTOTHENIC_ACID', 12, 'acido pantotenico', 'MG', 6, 2),
];

const MICRONUTRIENT_BY_CODE: ReadonlyMap<MicronutrientCode, MicronutrientDefinition> = new Map(
  MICRONUTRIENT_REGISTRY.map((entry) => [entry.code, entry]),
);

export function getMicronutrientDefinition(code: MicronutrientCode): MicronutrientDefinition {
  const definitionEntry = MICRONUTRIENT_BY_CODE.get(code);
  if (!definitionEntry) {
    throw new RangeError(`Unknown micronutrient code: ${code}`);
  }
  return definitionEntry;
}

/**
 * Soglia di "quantita' significativa" (Allegato XIII Parte A punto 2, richiamata
 * dall'art. 30(2)(f)): 15% VNR/100g per alimenti, 7,5% VNR/100ml per bevande,
 * 15% VNR per porzione se la confezione contiene una sola porzione.
 */
export const MICRONUTRIENT_SIGNIFICANT_THRESHOLD_PERCENT_FOOD = 15;
export const MICRONUTRIENT_SIGNIFICANT_THRESHOLD_PERCENT_BEVERAGE = 7.5;
export const MICRONUTRIENT_SIGNIFICANT_THRESHOLD_PERCENT_SINGLE_PORTION = 15;

export function isMicronutrientSignificant(
  percentNrvAdultsPer100: number,
  isBeverage: boolean,
): boolean {
  const threshold = isBeverage
    ? MICRONUTRIENT_SIGNIFICANT_THRESHOLD_PERCENT_BEVERAGE
    : MICRONUTRIENT_SIGNIFICANT_THRESHOLD_PERCENT_FOOD;
  return percentNrvAdultsPer100 >= threshold;
}

/**
 * Note di scope da riportare in UI/documentazione ovunque il registro sia
 * esposto all'utente (art. 29(1)(a) Reg. 1169/2011 esclude gli integratori dal
 * capo sulla dichiarazione nutrizionale; il motore per-100g e le soglie 15%/
 * 7,5% non sono idonei per un integratore, che dichiara per dose giornaliera
 * con tolleranza -20% ex Dir. 2002/46/CE). I VNR sono "(adulti)": l'Allegato
 * XIII non fornisce valori per lattanti/prima infanzia (Reg. 609/2013).
 */
export const MICRONUTRIENT_REGISTRY_SCOPE_NOTE =
  'VNR e soglie ai sensi del Reg. UE 1169/2011, Allegato XIII (adulti). Non idoneo per integratori alimentari (Dir. 2002/46/CE: dichiarazione per dose giornaliera, tolleranza -20%) ne per alimenti per lattanti/prima infanzia (Reg. UE 609/2013).' as const;
